// ─── time/format ───────────────────────────────────────────────────────────────
// Display + edit-prefill formatting. Intl ONLY — zero date-fns, so web bundles
// this without pulling `date-fns-tz` (hard dependency rule: this file must never
// import `./tz-core.js`).
//
// The display date format is FIXED `dd-mm-yyyy` (24h time) on purpose: a date
// format is a regional/institutional convention, NOT a linguistic one, so it must
// NOT follow the UI language (es→dd-mm / en→mm-dd would be wrong). For Chile —
// and every current tenant — dd-mm-yyyy is correct. (When tenant localization
// lands, the format becomes a tenant config field; until then it is fixed here.)
/** Wall-clock parts of an instant, rendered IN `tz` via Intl (locale-independent). */
function zonedParts(iso, tz) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(new Date(iso));
    const get = (t) => parts.find((p) => p.type === t)?.value ?? '';
    let h = get('hour');
    if (h === '24')
        h = '00'; // some engines emit '24' for midnight under hour12:false
    return { y: get('year'), mo: get('month'), d: get('day'), h, mi: get('minute') };
}
export const DEFAULT_DATE_FORMAT = 'dd-mm-yyyy';
export const DEFAULT_TIME_FORMAT = '24h';
/** Render an already-zoned 24h `h`/`mi` pair per the tenant clock convention. */
function formatTime(h, mi, tf) {
    if (tf === '24h')
        return `${h}:${mi}`;
    const hour = Number(h);
    const period = hour < 12 ? 'AM' : 'PM';
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${mi} ${period}`;
}
/** Order the day/month/year parts per the tenant format. `dd-mm-yyyy`/`mm-dd-yyyy`
 *  join with `-`; `yyyy-mm-dd` is the ISO order. */
function orderDate(p, fmt) {
    switch (fmt) {
        case 'mm-dd-yyyy':
            return `${p.mo}-${p.d}-${p.y}`;
        case 'yyyy-mm-dd':
            return `${p.y}-${p.mo}-${p.d}`;
        case 'dd-mm-yyyy':
        default:
            return `${p.d}-${p.mo}-${p.y}`;
    }
}
/** Display an instant in `tz` as `<date> <time>`: the date ordered per `fmt`
 *  (default `dd-mm-yyyy`) and the time per `tf` (default 24h). */
export function formatInstant(iso, tz, fmt = DEFAULT_DATE_FORMAT, tf = DEFAULT_TIME_FORMAT) {
    const p = zonedParts(iso, tz);
    return `${orderDate(p, fmt)} ${formatTime(p.h, p.mi, tf)}`;
}
/** Convert an instant to the `yyyy-MM-ddTHH:mm` wall-clock string in `tz` that a
 *  `<input type="datetime-local">` expects — so editing prefills the seminary's
 *  local time, not the browser's. */
export function instantToZonedWall(iso, tz) {
    const p = zonedParts(iso, tz);
    return `${p.y}-${p.mo}-${p.d}T${p.h}:${p.mi}`;
}
/** Display a calendar day (`yyyy-MM-dd`) ordered per `fmt` (default `dd-mm-yyyy`).
 *  Pure string reformat — never constructs a Date, so no zone can shift a day-only
 *  value. */
export function formatDateOnly(dateOnly, fmt = DEFAULT_DATE_FORMAT) {
    const [y, mo, d] = dateOnly.split('-');
    return orderDate({ y: y ?? '', mo: mo ?? '', d: d ?? '' }, fmt);
}
/** Today's calendar day as `yyyy-MM-dd` in `tz`, via Intl. Web-safe sibling of
 *  tz-core's `todayInZone` (no date-fns) for day-only comparisons like section
 *  lifecycle: "today" must be the tenant's calendar day, not the browser's, so a
 *  late-evening user in Chile (UTC-3) is not already "tomorrow". `en-CA` renders
 *  a date as `yyyy-MM-dd`. */
export function todayInZoneWeb(tz, now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
//# sourceMappingURL=format.js.map